/**
 * Fiscal obligations (E01, ALC-4760): the system catalog and the accountant's applicability
 * decisions per Taxpayer (federal obligations) or establishment (municipal obligations).
 */
import type { ObligationChannelStatus } from './fiscal-channels.js';
import type {
  FiscalObligationLevel,
  FiscalObligationPeriodicity,
  ObligationApplicabilityOrigin,
  ObligationApplicabilityState,
  ObligationDeadlineAdjustment,
  ObligationDeadlineSource,
  ObligationEvidenceEnvironment,
  ObligationOccurrenceEventKind,
  ObligationOccurrenceState,
  ObligationPaymentState,
  ObligationResponsibleRole,
  ObligationScopeType,
} from './generated/enums.js';

/** The first day of the fiscal obligations calendar: nothing is decided for earlier days. */
export const FISCAL_OBLIGATIONS_START_DATE = '2027-01-01';

/** Stable error codes of the fiscal obligations service, as the API answers them. */
export const FISCAL_OBLIGATION_ERROR_CODES = [
  'OBLIGATION_NOT_FOUND',
  'OBLIGATION_DATE_INVALID',
  'OBLIGATION_BEFORE_CALENDAR_START',
  'OBLIGATION_WAIVER_REQUIRES_BASIS_AND_EVIDENCE',
  'OBLIGATION_OUT_OF_SCOPE_REQUIRES_REASON',
  'OBLIGATION_SCREENING_ONLY_CANNOT_APPLY',
  'OBLIGATION_SCOPE_MISMATCH',
  'OBLIGATION_APPLICABILITY_PERIOD_LOCKED',
  'OBLIGATION_APPLICABILITY_OVERLAP',
  'OBLIGATION_RETRY',
  'OBLIGATION_RESPONSIBLE_NOT_ELIGIBLE',
  'TAXPAYER_ACCOUNTANT_DETAILS_REQUIRED',
  'TAXPAYER_ACCOUNTANT_CPF_INVALID',
  'TAXPAYER_ACCOUNTANT_MISSING',
  'TAXPAYER_ACCOUNTANT_OVERLAP',
  'TAXPAYER_ACCOUNTANT_PERIOD_LOCKED',
  'OBLIGATION_SCREENING_QUESTION_UNKNOWN',
  'OBLIGATION_SUGGESTION_NOT_DECISIVE',
  'OBLIGATION_SUGGESTION_CHANGED',
  'OBLIGATION_SETUP_COPY_TARGET_INVALID',
  'OBLIGATION_DEADLINE_RULE_INVALID',
  'OBLIGATION_NO_PERIODS',
  'OBLIGATION_DEADLINE_OVERRIDE_NOT_FOUND',
  'OBLIGATION_OCCURRENCE_NOT_FOUND',
  'OBLIGATION_TRANSITION_NOT_ALLOWED',
  'OBLIGATION_NOT_APPLICABLE_BY_DECISION',
  'OBLIGATION_EVIDENCE_REQUIRED',
  'OBLIGATION_EVIDENCE_NOT_PRODUCTION',
  'OBLIGATION_RECTIFICATION_NOT_ALLOWED',
  'OBLIGATION_RECTIFICATION_REASON_REQUIRED',
  'OBLIGATION_PAYMENT_NOT_APPLICABLE',
  'OBLIGATION_PAYMENT_STATE_UNCHANGED',
  'OBLIGATION_PAYMENT_DATE_REQUIRED',
  'OBLIGATION_PAYMENT_EVIDENCE_REQUIRED',
  'OBLIGATION_ATTACHMENT_NOT_FOUND',
  'OBLIGATION_ATTACHMENT_LOCKED',
  'OBLIGATION_ATTACHMENT_INVALID',
  'CHANNEL_CREDENTIAL_NOT_FOUND',
  'CHANNEL_CREDENTIAL_ENTITY_MISMATCH',
  'CHANNEL_ENABLEMENT_NOT_FOUND',
  'POWER_OF_ATTORNEY_DETAILS_REQUIRED',
  'POWER_OF_ATTORNEY_TAX_ID_INVALID',
  'POWER_OF_ATTORNEY_COVERAGE_INVALID',
  'POWER_OF_ATTORNEY_NOT_FOUND',
  'POWER_OF_ATTORNEY_END_INVALID',
  'MAILBOX_NOT_FOUND',
  'MAILBOX_ALREADY_EXISTS',
  'MAILBOX_LABEL_REQUIRED',
  'MAILBOX_LABEL_INVALID',
  'MAILBOX_INACTIVE',
  'MAILBOX_RULE_INVALID',
  'MAILBOX_RULE_DATE_INVALID',
  'MAILBOX_RULE_OVERLAP',
  'MAILBOX_RULE_PERIOD_LOCKED',
  'MAILBOX_RULE_NOT_IN_FORCE',
  'COMMUNICATION_NOT_FOUND',
  'COMMUNICATION_DETAILS_REQUIRED',
  'COMMUNICATION_DATE_INVALID',
  'COMMUNICATION_STATUS_INVALID',
  'COMMUNICATION_OUTCOME_REQUIRED',
] as const;
export type FiscalObligationErrorCode = (typeof FISCAL_OBLIGATION_ERROR_CODES)[number];

export interface FiscalObligationCatalogItem {
  code: string;
  name: string;
  authority: string;
  tax: string;
  level: FiscalObligationLevel;
  periodicity: FiscalObligationPeriodicity;
  /** Day of the month it falls due, `-1` for the last day, `null` when no fixed rule exists. */
  deadlineDay: number | null;
  /** The day counts business days: the nth, or the last when `deadlineDay` is `-1`. */
  deadlineBusinessDay: boolean;
  /** A fixed day moved to the previous or next business day when it is not one (DCTFWeb). */
  deadlineAdjustment: ObligationDeadlineAdjustment;
  deadlineMonthOffset: number;
  deadlineRuleSource: string;
  deadlineRuleVersion: string;
  paymentObligationCode: string | null;
  screeningOnly: boolean;
}

export interface ObligationApplicabilityDecision {
  applicabilityId: string;
  scopeType: ObligationScopeType;
  taxpayerId: string | null;
  entityId: string | null;
  obligationCode: string;
  validFrom: string;
  validTo: string | null;
  state: ObligationApplicabilityState;
  basisCode: string | null;
  basisDescription: string | null;
  evidenceCode: string | null;
  evidenceDescription: string | null;
  reason: string | null;
  origin: ObligationApplicabilityOrigin;
  createdByUserId: string | null;
  createdAt: string;
}

export interface DecideObligationApplicabilityRequest {
  obligationCode: string;
  validFrom: string;
  validTo?: string | null;
  state: ObligationApplicabilityState;
  /** Legal basis of a waiver (`WAIVED_WITH_BASIS`). */
  basisCode?: string;
  basisDescription?: string;
  /** Evidence of a waiver (`WAIVED_WITH_BASIS`). */
  evidenceCode?: string;
  evidenceDescription?: string;
  /** Why an obligation is flagged out of scope (`OUT_OF_SCOPE_FLAGGED`), or any note. */
  reason?: string;
}

/**
 * Why a decision cannot be edited from this establishment's page.
 * `NO_COMPANY_ACCESS`: the head office, but the caller's scope lacks some establishment of the company.
 */
export type ObligationReadOnlyReason = 'NOT_HEAD_OFFICE' | 'NO_TAXPAYER' | 'NO_COMPANY_ACCESS';

export interface EntityObligationItem {
  obligation: FiscalObligationCatalogItem;
  scopeType: ObligationScopeType;
  /** The Taxpayer (federal) or the establishment (municipal) that decides it; null without a Taxpayer. */
  scopeId: string | null;
  editable: boolean;
  readOnlyReason: ObligationReadOnlyReason | null;
  /** The decision covering `asOf`, or null when none has been made. */
  current: ObligationApplicabilityDecision | null;
  /** The state at `asOf`: a missing decision reads as pending, never as applicable. */
  effectiveState: ObligationApplicabilityState;
  /** Every live decision of this scope and obligation, oldest first. */
  segments: ObligationApplicabilityDecision[];
}

export interface EntityObligationsResponse {
  entityId: string;
  asOf: string;
  taxpayer: {
    taxpayerId: string;
    cnpjRoot: string;
    name: string;
    headOfficeEntityId: string | null;
    isHeadOffice: boolean;
  } | null;
  items: EntityObligationItem[];
  /** Obligations this establishment's page can decide that are still pending at `asOf`. */
  pendingCount: number;
}

/** The four stages an obligation moves through, in order: each has one responsible user. */
export const OBLIGATION_RESPONSIBLE_ROLES = [
  'PREPARER',
  'APPROVER',
  'TRANSMITTER',
  'PAYER',
] as const satisfies readonly ObligationResponsibleRole[];

/** Where a role's holder comes from: the obligation's own override, the Taxpayer default, or no one. */
export type ObligationResponsibleSource = 'OVERRIDE' | 'DEFAULT' | 'NONE';

/**
 * Why a role has no responsible: nobody was named, or the one named is gone, inactive, or cannot
 * reach the establishment any more (checked when read, not only when assigned).
 */
export type ObligationResponsibleVacancy =
  'UNASSIGNED' | 'USER_NOT_FOUND' | 'USER_INACTIVE' | 'USER_NO_ENTITY_ACCESS';

export interface ObligationResponsibleSlot {
  role: ObligationResponsibleRole;
  source: ObligationResponsibleSource;
  /** Who was named (override first, then the default), even when they can no longer act. */
  assignedUserId: string | null;
  assignedUserName: string | null;
  /** Who acts: the assigned user when active with access to the establishment, otherwise null. */
  responsibleUserId: string | null;
  vacancy: ObligationResponsibleVacancy | null;
}

export interface EntityObligationResponsibleItem {
  obligationCode: string;
  obligationName: string;
  scopeType: ObligationScopeType;
  /** Whether this establishment's page may set the overrides (federal ones only on the head office). */
  editable: boolean;
  readOnlyReason: ObligationReadOnlyReason | null;
  /** The applicability decision in force: only an applicable obligation counts as unassigned. */
  effectiveState: ObligationApplicabilityState;
  /** The four roles, resolved: override, then the Taxpayer default. */
  slots: ObligationResponsibleSlot[];
  vacantRoles: ObligationResponsibleRole[];
}

export interface EntityObligationResponsiblesResponse {
  entityId: string;
  asOf: string;
  taxpayer: EntityObligationsResponse['taxpayer'];
  /**
   * The establishment whose user access decides who may act on a Taxpayer-level obligation: the head
   * office (or this establishment when it has none). A municipal obligation uses its own establishment.
   */
  taxpayerAccessEntityId: string | null;
  defaultsEditable: boolean;
  defaultsReadOnlyReason: ObligationReadOnlyReason | null;
  defaults: ObligationResponsibleSlot[];
  items: EntityObligationResponsibleItem[];
  /** Applicable obligations with at least one vacant role. */
  unassignedCount: number;
}

export interface SetObligationResponsibleRequest {
  role: ObligationResponsibleRole;
  /** The user to hold the role; null clears it (an override falls back to the Taxpayer default). */
  userId: string | null;
  /** Why the role changes hands; kept with the author and the date in the audit trail. */
  reason?: string;
}

/** What a write changed: the holder of one role, for the Taxpayer default or one obligation. */
export interface ObligationResponsibleAssignment {
  level: 'DEFAULT' | 'OVERRIDE';
  /** The obligation of an override; null for a Taxpayer default. */
  obligationCode: string | null;
  role: ObligationResponsibleRole;
  userId: string | null;
  previousUserId: string | null;
}

export interface ObligationResponsibleCandidate {
  userId: string;
  name: string;
  email: string;
}

export interface ObligationResponsibleCandidatesResponse {
  entityId: string;
  candidates: ObligationResponsibleCandidate[];
}

/** A person named as a responsible where the caller can see it: an option of the central's filter. */
export interface ObligationResponsiblePerson {
  userId: string;
  name: string;
}

export interface ObligationResponsiblePeopleResponse {
  people: ObligationResponsiblePerson[];
}

export interface VacantObligationRole {
  role: ObligationResponsibleRole;
  reason: ObligationResponsibleVacancy;
}

export interface UnassignedObligationItem {
  entityId: string;
  entityName: string;
  taxpayerId: string | null;
  obligationCode: string;
  obligationName: string;
  scopeType: ObligationScopeType;
  vacantRoles: VacantObligationRole[];
}

export interface UnassignedObligationsResponse {
  asOf: string;
  items: UnassignedObligationItem[];
}

export interface TaxpayerAccountantResponse {
  accountantId: string;
  taxpayerId: string;
  name: string;
  crc: string;
  /** The CPF with all but its middle digits hidden; the full number is never sent back. */
  cpfMasked: string;
  validFrom: string;
  validTo: string | null;
  createdByUserId: string | null;
  createdAt: string;
}

export interface EntityAccountantResponse {
  entityId: string;
  taxpayerId: string | null;
  isHeadOffice: boolean;
  headOfficeEntityId: string | null;
  /** Whether this caller may register an accountant here: the head office, with the whole company in scope. */
  canWrite: boolean;
  at: string;
  segments: TaxpayerAccountantResponse[];
  /** The accountant in force on `at`, or null. */
  resolved: TaxpayerAccountantResponse | null;
}

export interface StartTaxpayerAccountantRequest {
  name: string;
  crc: string;
  /** Eleven digits, with or without punctuation. */
  cpf: string;
  validFrom: string;
  validTo?: string | null;
}

// ---- Occurrences, gaps and readiness: the obligations central (ALC-4768) ----

/**
 * The states of an occurrence that are still work: nothing was transmitted yet. `NOT_APPLICABLE`
 * and the delivered states (`TRANSMITTED`, `ACCEPTED`, `CLOSED`) are not.
 */
export const OBLIGATION_OCCURRENCE_OPEN_STATES = [
  'AWAITING_FACTS',
  'PENDING',
  'PREPARED',
  'VALIDATED',
  'RECTIFICATION_PENDING',
] as const satisfies readonly ObligationOccurrenceState[];

/**
 * The open states whose original due date can still make them overdue. A rectification in progress
 * is open work, but the due date belongs to the delivery already accepted: the rectification never
 * becomes permanently overdue because of it.
 */
export const OBLIGATION_OCCURRENCE_OVERDUE_STATES = [
  'AWAITING_FACTS',
  'PENDING',
  'PREPARED',
  'VALIDATED',
] as const satisfies readonly ObligationOccurrenceState[];

/**
 * The views of the central. `TODO`: open, not late. `OVERDUE`: open, past due. `ALL`: every
 * occurrence. `DUE_SOON`: the `TODO` occurrences due within the next
 * {@link OBLIGATION_DUE_SOON_DAYS} days. `OPEN`: `TODO` and `OVERDUE` together. `UNASSIGNED`: the
 * `OPEN` occurrences with a role nobody can act on.
 */
export const OBLIGATION_OCCURRENCE_VIEWS = [
  'TODO',
  'OVERDUE',
  'ALL',
  'DUE_SOON',
  'UNASSIGNED',
  'OPEN',
] as const;
/** How many days ahead `DUE_SOON` looks: due today up to this many days after today. */
export const OBLIGATION_DUE_SOON_DAYS = 7;
export type ObligationOccurrenceView = (typeof OBLIGATION_OCCURRENCE_VIEWS)[number];

export interface ObligationOccurrenceResponsible {
  role: ObligationResponsibleRole;
  /** Who acts on the role, or null when nobody can. */
  userId: string | null;
  userName: string | null;
}

export interface ObligationOccurrenceItem {
  occurrenceId: string;
  obligationCode: string;
  obligationName: string;
  scopeType: ObligationScopeType;
  taxpayerId: string | null;
  taxpayerName: string | null;
  /** The establishment the occurrence is listed on: itself, or the Taxpayer's head office. */
  entityId: string;
  entityName: string;
  periodLabel: string;
  periodStart: string;
  periodEnd: string;
  /** Null when the rule has no fixed day. */
  dueDate: string | null;
  deadlineSource: ObligationDeadlineSource;
  deadlineRuleSource: string;
  deadlineRuleVersion: string;
  state: ObligationOccurrenceState;
  /** The delivery version in progress: 1, then one more per rectification of an accepted delivery. */
  version: number;
  /** The payment axis, independent of the delivery state. */
  paymentState: ObligationPaymentState;
  /** Open and past its due date on the day of the answer. */
  overdue: boolean;
  /** Roles nobody can act on (never named, user inactive, or no access to the establishment). */
  vacantRoles: VacantObligationRole[];
  responsibles: ObligationOccurrenceResponsible[];
  /** The channel this obligation would be delivered through today (ALC-4770). */
  channel: ObligationChannelStatus;
  /**
   * Whether the caller may write the company's (Taxpayer's) data: every establishment of the
   * Taxpayer, past and present, is in its entity scope. True with no Taxpayer.
   */
  canWriteCompany: boolean;
}

export interface ObligationOccurrencesQuery {
  view?: ObligationOccurrenceView;
  /** The companies: every establishment of these Taxpayers. */
  taxpayerIds?: string[];
  /** The establishments the occurrences are listed on. */
  entityIds?: string[];
  obligationCodes?: string[];
  /** Occurrences whose period ends on or after / on or before these days. */
  periodFrom?: string;
  periodTo?: string;
  states?: ObligationOccurrenceState[];
  /** Occurrences any of these users holds a role of. */
  responsibleUserIds?: string[];
  dueFrom?: string;
  dueTo?: string;
  page?: number;
  pageSize?: number;
}

export interface ObligationOccurrencesResponse {
  asOf: string;
  items: ObligationOccurrenceItem[];
  total: number;
  page: number;
  pageSize: number;
  /**
   * View counts under the same filters, but the view and the state. `gaps` honors only the
   * company, establishment and obligation filters.
   */
  counts: {
    todo: number;
    overdue: number;
    gaps: number;
    all: number;
    dueSoon: number;
    unassigned: number;
  };
}

/** Marks several obligations "applicable" at once, from the central's list of gaps. */
export interface ApplyObligationsRequest {
  /** The first day the decisions hold. */
  validFrom: string;
  /** Each obligation as the gap lists it: the establishment it is listed on, and its code. */
  items: { entityId: string; obligationCode: string }[];
}

export type AppliedObligationResult =
  | {
      entityId: string;
      obligationCode: string;
      applied: true;
      decision: ObligationApplicabilityDecision;
    }
  | {
      entityId: string;
      obligationCode: string;
      applied: false;
      errorCode: string;
      message: string;
    };

export interface ApplyObligationsResponse {
  results: AppliedObligationResult[];
  appliedCount: number;
}

export interface ObligationGapItem {
  /** Stable per scope and obligation, for row keys. */
  gapKey: string;
  scopeType: ObligationScopeType;
  taxpayerId: string | null;
  taxpayerName: string | null;
  /** The establishment the gap is listed on: itself, or the Taxpayer's head office. */
  entityId: string;
  entityName: string;
  obligationCode: string;
  obligationName: string;
  state: Extract<ObligationApplicabilityState, 'PENDING_CLASSIFICATION' | 'OUT_OF_SCOPE_FLAGGED'>;
  /** False when nobody decided yet: the obligation reads as pending. */
  hasDecision: boolean;
  validFrom: string | null;
  reason: string | null;
  /**
   * Whether the caller may write the company's (Taxpayer's) data: every establishment of the
   * Taxpayer, past and present, is in its entity scope. True for an establishment's own (municipal) gap.
   */
  canWriteCompany: boolean;
}

export interface ObligationGapsQuery {
  taxpayerIds?: string[];
  entityIds?: string[];
  obligationCodes?: string[];
  states?: ObligationGapItem['state'][];
  page?: number;
  pageSize?: number;
}

export interface ObligationGapsResponse {
  asOf: string;
  items: ObligationGapItem[];
  total: number;
  page: number;
  pageSize: number;
}

/** Why a company profile is incomplete; each reason is fixed on one tab of the establishment. */
export type ObligationProfileGap =
  | 'NO_TAXPAYER'
  | 'NO_HEAD_OFFICE'
  | 'NO_FISCAL_HISTORY'
  | 'CBS_UNCLASSIFIED'
  | 'IBS_UNCLASSIFIED'
  | 'NO_ACCOUNTANT';

export interface ObligationReadinessItem {
  /** Null for an establishment that belongs to no Taxpayer. */
  taxpayerId: string | null;
  companyName: string;
  /** The head office (or the establishment itself with no Taxpayer): where the profile is fixed. */
  entityId: string;
  profileGaps: ObligationProfileGap[];
  unansweredQuestions: number;
  /** The unanswered questions about the company itself (Taxpayer level), among `unansweredQuestions`. */
  unansweredCompanyQuestions: number;
  /** The establishment with unanswered questions to open (the head office when it has any). */
  screeningEntityId: string | null;
  /** Obligations with no decision, or still pending classification. */
  undecidedObligations: number;
  /** Applicable obligations with a role nobody can act on. */
  unassignedObligations: number;
  /**
   * Whether the caller may write the company's (Taxpayer's) data: every establishment of the
   * Taxpayer, past and present, is in its entity scope. True with no Taxpayer.
   */
  canWriteCompany: boolean;
  ready: boolean;
}

export interface ObligationReadinessResponse {
  asOf: string;
  items: ObligationReadinessItem[];
  readyCount: number;
}

export interface ObligationDeadlineOverride {
  overrideId: string;
  scopeType: ObligationScopeType;
  obligationCode: string;
  /** Applies to the periods that end on or after this day. */
  validFrom: string;
  /** Day of the month, `-1` for the last day. */
  deadlineDay: number;
  deadlineBusinessDay: boolean;
  deadlineMonthOffset: number;
  source: string;
}

export interface SetObligationDeadlineOverrideRequest {
  obligationCode: string;
  validFrom: string;
  deadlineDay: number;
  deadlineBusinessDay?: boolean;
  deadlineMonthOffset?: number;
  /** What supports the rule, for example the municipal decree. */
  source: string;
}

export interface EntityDeadlineOverridesResponse {
  entityId: string;
  overrides: ObligationDeadlineOverride[];
}

export interface GenerateObligationOccurrencesResponse {
  created: number;
  updated: number;
  markedNotApplicable: number;
}

// ---- Evidence, payment and rectification of an occurrence (ALC-4769) ----

/**
 * The delivery steps, in order. An occurrence moves one step at a time; `RECTIFICATION_PENDING`
 * (opened from an accepted delivery) re-enters at `PREPARED` for the next version, and
 * `NOT_APPLICABLE` is only ever the effect of an applicability decision, never a step.
 */
export const OBLIGATION_STATE_TRANSITIONS: Readonly<
  Partial<Record<ObligationOccurrenceState, ObligationOccurrenceState>>
> = {
  AWAITING_FACTS: 'PENDING',
  PENDING: 'PREPARED',
  PREPARED: 'VALIDATED',
  VALIDATED: 'TRANSMITTED',
  TRANSMITTED: 'ACCEPTED',
  ACCEPTED: 'CLOSED',
  RECTIFICATION_PENDING: 'PREPARED',
};

/** What a step needs besides the environment (which must be `PRODUCTION`). */
export type ObligationEvidenceField =
  'fileVersion' | 'layoutVersion' | 'protocolNumber' | 'authorityReturn';

/**
 * The steps that need evidence, with the fields each one asks for: the file and layout version that
 * passed validation, the protocol or receipt the authority gave on receiving it, and the
 * authority's answer that accepted it.
 */
export const OBLIGATION_EVIDENCE_REQUIREMENTS: Readonly<
  Partial<Record<ObligationOccurrenceState, readonly ObligationEvidenceField[]>>
> = {
  VALIDATED: ['fileVersion', 'layoutVersion'],
  TRANSMITTED: ['protocolNumber'],
  ACCEPTED: ['authorityReturn'],
};

export const OBLIGATION_PAYMENT_STATES = [
  'NOT_DUE',
  'DUE',
  'PAID',
  'INSTALLMENT',
] as const satisfies readonly ObligationPaymentState[];

export const OBLIGATION_EVIDENCE_ENVIRONMENTS = [
  'PRODUCTION',
  'RESTRICTED_PRODUCTION',
  'SIMULATED',
] as const satisfies readonly ObligationEvidenceEnvironment[];

/** The proof of a delivery step. Files are uploaded first and referenced by id. */
export interface ObligationEvidenceInput {
  environment: ObligationEvidenceEnvironment;
  /** The protocol or receipt number the authority gave. */
  protocolNumber?: string;
  fileVersion?: string;
  layoutVersion?: string;
  /** What the authority answered (accepted, rejected with reasons, ...). */
  authorityReturn?: string;
  /** Files uploaded to this occurrence for the version in progress and not yet used. */
  attachmentIds?: string[];
}

export interface AdvanceObligationOccurrenceRequest {
  to: ObligationOccurrenceState;
  reason?: string;
  /** Required to reach `VALIDATED`, `TRANSMITTED` or `ACCEPTED`. */
  evidence?: ObligationEvidenceInput;
}

/** Records evidence (a test run, a simulated answer) without moving the occurrence. */
export interface RecordObligationEvidenceRequest {
  evidence: ObligationEvidenceInput;
  reason?: string;
}

export interface RecordObligationPaymentRequest {
  paymentState: ObligationPaymentState;
  reason?: string;
  /** The day the payment was made (`YYYY-MM-DD`). Required to record `PAID`. */
  paidOn?: string;
  /** The payment guide or receipt number. `PAID` needs this or a file. */
  protocolNumber?: string;
  attachmentIds?: string[];
}

export interface OpenObligationRectificationRequest {
  reason: string;
}

export interface ObligationEvidenceAttachment {
  attachmentId: string;
  originalName: string;
  mimeType: string;
  fileSizeBytes: number;
  uploadedAt: string;
}

export interface ObligationOccurrenceHistoryEvent {
  eventId: string;
  /** The delivery version the action belongs to. */
  version: number;
  kind: ObligationOccurrenceEventKind;
  fromState: ObligationOccurrenceState | null;
  toState: ObligationOccurrenceState | null;
  fromPaymentState: ObligationPaymentState | null;
  toPaymentState: ObligationPaymentState | null;
  reason: string | null;
  environment: ObligationEvidenceEnvironment | null;
  protocolNumber: string | null;
  fileVersion: string | null;
  layoutVersion: string | null;
  authorityReturn: string | null;
  /** The day the payment was made, for a change to `PAID`. */
  paymentDate: string | null;
  attachments: ObligationEvidenceAttachment[];
  actorUserId: string | null;
  actorName: string | null;
  createdAt: string;
}

/** One occurrence with its full history, newest action last, and the files waiting to be used. */
export interface ObligationOccurrenceDetail {
  occurrence: ObligationOccurrenceItem;
  history: ObligationOccurrenceHistoryEvent[];
  /** Uploaded to the occurrence and not yet used by any action. */
  pendingAttachments: ObligationEvidenceAttachment[];
}
